/**
 * Backup Warning Component
 *
 * Shows backup reminder and download option
 */

import { Download, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBrowserWallet } from "@nullaone/wallet-react";

export function BackupWarning() {
  const { downloadBackup, markBackedUp, closeModal } = useBrowserWallet();

  const handleDownload = async () => {
    try {
      await downloadBackup();
      await markBackedUp();
      closeModal();
    } catch (error) {
      console.error("Failed to download backup:", error);
    }
  };

  const handleSkip = () => {
    closeModal();
  };

  return (
    <div className="space-y-6 py-4">
      {/* Warning banner */}
      <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-600">
              Back Up Your Wallet
            </p>
            <p className="text-xs text-amber-600/80">
              Download an encrypted backup of your wallet. You&apos;ll need your
              biometric authentication on this device to restore it.
            </p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="space-y-4 text-sm text-muted-foreground">
        <p>
          Your wallet backup is encrypted with a key derived from your biometric
          authentication (TouchID/FaceID). This means:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li>The backup can only be restored on this device</li>
          <li>You&apos;ll need your biometric to decrypt it</li>
          <li>
            If you lose access to this device, use your recovery phrase instead
          </li>
        </ul>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        <Button className="w-full gap-2" size="lg" onClick={handleDownload}>
          <Download className="h-4 w-4" />
          Download Encrypted Backup
        </Button>

        <Button variant="ghost" className="w-full" onClick={handleSkip}>
          Skip for now
        </Button>
      </div>

      {/* Reminder */}
      <p className="text-xs text-center text-muted-foreground">
        You can always download a backup later from wallet settings
      </p>
    </div>
  );
}

/**
 * Inline backup warning banner for use in other components
 */
export function BackupWarningBanner() {
  const { needsBackup, openModal } = useBrowserWallet();

  if (!needsBackup) return null;

  return (
    <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="text-sm text-amber-600">Wallet not backed up</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="text-amber-600 border-amber-500/50"
          onClick={() => openModal("backup")}
        >
          Backup Now
        </Button>
      </div>
    </div>
  );
}
